<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

/**
 * The application's own tooltips (D-310), in place of the browser's: whatever has a `title` shows
 * it in a readable box — larger type, wrapped lines, next to it — after a short rest of the pointer.
 * From the moment the pointer is over it until it leaves, the browser has no title to show: the
 * element's and every enclosing one's are held aside (data-tip-held) — through a click, a key or a
 * scroll, which hide the box only, and through a re-render that writes a title back — and given
 * back when the pointer leaves; an icon named by its title keeps the name meanwhile (aria-label).
 * A touch shows none: the press that follows hides it before its time. Inside a text being edited
 * the browser's own (its editor would redraw a title held aside, endlessly).
 */
const tip = ref(null)
const box = ref(null)
let target = null
let held = []
let timer = null
let watcher = null
const DELAY = 350

function heldText(el) {
  return el.getAttribute('title') || el.dataset.tipHeld || ''
}
// A title set while held (a re-render, a changed text) goes aside too.
function hold(el) {
  if (el.hasAttribute('title')) {
    el.dataset.tipHeld = el.getAttribute('title')
    el.removeAttribute('title')
    // An icon alone is named by its title: the name stays while the title is aside.
    if (!el.textContent.trim() && !el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
      el.setAttribute('aria-label', el.dataset.tipHeld)
      el.dataset.tipNamed = ''
    }
  }
  if (el.dataset.tipHeld !== undefined && !held.includes(el)) held.push(el)
}
function release() {
  clearTimeout(timer)
  watcher?.disconnect()
  for (const el of held) {
    if (el.dataset.tipHeld === undefined) continue
    if (!el.hasAttribute('title')) el.setAttribute('title', el.dataset.tipHeld)
    if (el.dataset.tipNamed !== undefined) {
      el.removeAttribute('aria-label')
      delete el.dataset.tipNamed
    }
    delete el.dataset.tipHeld
  }
  held = []
  target = null
  tip.value = null
}
function over(e) {
  const found = e.target instanceof Element ? e.target.closest('[title], [data-tip-held]') : null
  // Not in a text being edited: its editor draws its titles itself and writes one held aside
  // back at once, over and over; there the browser's own title shows.
  const el = found?.isContentEditable ? null : found
  if (el === target) return
  // Within the one held (a child without its own title): nothing changes.
  if (target && el === null && target.contains(e.target)) return
  release()
  if (!(el instanceof HTMLElement) || !heldText(el).trim()) return
  target = el
  const text = heldText(el)
  for (let x = el; x; x = x.parentElement) if (x.hasAttribute('title') || x.dataset.tipHeld !== undefined) hold(x)
  watcher = new MutationObserver((changes) => {
    for (const c of changes) if (c.target instanceof HTMLElement && c.target.hasAttribute('title') && c.target.contains(target)) hold(c.target)
  })
  watcher.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['title'] })
  timer = setTimeout(() => {
    if (target !== el || !el.isConnected) return
    show(el, el.dataset.tipHeld || text)
  }, DELAY)
}
// Below it, centred, kept on the screen; above it when there is no room below.
function show(el, text) {
  const r = el.getBoundingClientRect()
  tip.value = { text, left: r.left + r.width / 2, top: r.bottom + 6, above: r.top - 6, place: 'below' }
  requestAnimationFrame(() => {
    const b = box.value?.getBoundingClientRect()
    if (!b || !tip.value) return
    const left = Math.min(Math.max(8, tip.value.left - b.width / 2), window.innerWidth - b.width - 8)
    const below = tip.value.top + b.height <= window.innerHeight - 8
    tip.value = { ...tip.value, x: left, y: below ? tip.value.top : Math.max(8, tip.value.above - b.height), place: below ? 'below' : 'above' }
  })
}
// A press, a key, a scroll: the box goes, the titles stay held while the pointer is there.
const hide = () => {
  clearTimeout(timer)
  tip.value = null
}
const left = (e) => !e.relatedTarget && release()
onMounted(() => {
  document.addEventListener('mouseover', over, true)
  document.addEventListener('mouseout', left, true)
  document.addEventListener('mousedown', hide, true)
  document.addEventListener('scroll', hide, true)
  document.addEventListener('keydown', hide, true)
  window.addEventListener('blur', release)
})
onBeforeUnmount(() => {
  release()
  document.removeEventListener('mouseover', over, true)
  document.removeEventListener('mouseout', left, true)
  document.removeEventListener('mousedown', hide, true)
  document.removeEventListener('scroll', hide, true)
  document.removeEventListener('keydown', hide, true)
  window.removeEventListener('blur', release)
})
</script>

<template>
  <Teleport to="body">
    <div v-if="tip" ref="box" role="tooltip" data-testid="app-tooltip"
         class="pointer-events-none fixed z-[1000] max-w-xs whitespace-pre-line rounded-lg bg-gray-900/95 px-3 py-2 text-xs leading-snug text-white shadow-lg dark:bg-gray-100/95 dark:text-gray-900"
         :style="{ left: `${tip.x ?? -9999}px`, top: `${tip.y ?? -9999}px` }">{{ tip.text }}</div>
  </Teleport>
</template>
